import { getBlob } from "../Services/ApiBase";
import { API_ENDPOINTS } from "../Services/ApiEndPoints";
import { getApiErrorMessage } from "./apiErrorMessage";
import { getBillingExportFileUrlFromResponse } from "./buildSaasBillingExportFilters";

function parseFilenameFromContentDisposition(header) {
  if (!header || typeof header !== "string") return null;
  const utf8 = /filename\*=UTF-8''([^;\s]+)/i.exec(header);
  if (utf8) {
    try {
      return decodeURIComponent(utf8[1]);
    } catch {
      return null;
    }
  }
  const quoted = /filename="([^"]+)"/i.exec(header);
  if (quoted) return quoted[1].trim();
  const plain = /filename=([^;\s]+)/i.exec(header);
  return plain ? plain[1].trim().replace(/^["']|["']$/g, "") : null;
}

async function blobToErrorMessage(blob) {
  try {
    const text = await blob.text();
    try {
      const j = JSON.parse(text);
      return j.message || j.error || j.data?.message || text || "Download failed";
    } catch {
      const t = text.trim();
      return t ? t.slice(0, 500) : "Download failed";
    }
  } catch {
    return "Download failed";
  }
}

/**
 * Returns a user-facing message if `parsed` looks like a standard API error JSON.
 */
function messageIfJsonErrorBody(parsed) {
  if (!parsed || typeof parsed !== "object") return null;
  if (parsed.success === false) {
    const m = parsed.message ?? parsed.error;
    return typeof m === "string" && m.trim() ? m.trim() : "Download failed";
  }
  if (typeof parsed.error === "string" && parsed.error.trim()) {
    return parsed.error.trim();
  }
  if (parsed.message && (parsed.statusCode >= 400 || parsed.status >= 400)) {
    return typeof parsed.message === "string" ? parsed.message.trim() : null;
  }
  return null;
}

function triggerFileDownload(blob, headers, fallbackFileName) {
  const cd = headers?.["content-disposition"] || headers?.["Content-Disposition"];
  const safeFallback =
    typeof fallbackFileName === "string" && fallbackFileName.trim() !== ""
      ? fallbackFileName.trim()
      : "export";
  const filename = parseFilenameFromContentDisposition(cd) || safeFallback;

  const objectUrl = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = objectUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(objectUrl);
}

/**
 * GET /billing/exports/:exportId/download — authenticated file download (CSV/XLSX).
 *
 * @param {string} exportId — export row UUID
 * @param {string} [fallbackFileName] — used if Content-Disposition has no filename
 */
export async function downloadBillingExportFile(exportId, fallbackFileName = "export") {
  const id = exportId != null ? String(exportId).trim() : "";
  if (!id) {
    throw new Error("Missing export id");
  }

  const url = API_ENDPOINTS.BILLING.EXPORT_DOWNLOAD(id);

  try {
    const response = await getBlob(url);
    const status = response.status ?? 0;
    const blob = response.data;

    if (!(blob instanceof Blob)) {
      throw new Error("Invalid response");
    }

    if (status < 200 || status >= 300) {
      throw new Error(await blobToErrorMessage(blob));
    }

    const contentType = String(
      response.headers?.["content-type"] || response.headers?.["Content-Type"] || ""
    );

    // JSON body: may be an error, a presigned S3 URL wrapper (same shape as POST /billing/exports), or mislabeled CSV bytes.
    if (contentType.includes("application/json") || contentType.includes("text/json")) {
      const text = await blob.text();
      let parsed;
      try {
        parsed = JSON.parse(text);
      } catch {
        triggerFileDownload(blob, response.headers, fallbackFileName);
        return;
      }
      const errMsg = messageIfJsonErrorBody(parsed);
      if (errMsg) {
        throw new Error(errMsg);
      }
      const presigned = getBillingExportFileUrlFromResponse(parsed);
      if (presigned) {
        window.open(presigned, "_blank", "noopener,noreferrer");
        return;
      }
      throw new Error("Download response did not include a file URL");
    }

    triggerFileDownload(blob, response.headers, fallbackFileName);
  } catch (err) {
    if (err === "Request canceled") throw err;
    const data = err?.response?.data;
    if (data instanceof Blob) {
      const msg = await blobToErrorMessage(data);
      throw new Error(msg, { cause: err });
    }
    throw new Error(getApiErrorMessage(err, "Download failed"), { cause: err });
  }
}
